import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService } from '../services/authService';
import { LoginRequest, User, LoginResponse } from '../types';
import { setSharedAuthCookie, clearSharedAuthCookie } from '../services/cookieAuth';
import {
  clearLastActivityAt,
  expireStalePersistedSession,
  writeLastActivityAt,
} from '../utils/sessionInactivity';

export function mapLoginResponseToUser(response: LoginResponse): User {
  return {
    userId: response.userId,
    username: response.username,
    loginHandle: response.loginHandle ?? response.username,
    displayUsername: response.displayUsername ?? response.username,
    hasAdvancedMailbox: response.hasAdvancedMailbox ?? false,
    accountTypeCode: response.accountTypeCode,
    isBusinessAccount: (response.isBusinessAccount ?? false) || (response.accountTypeCode?.toUpperCase() === 'BUSINESS'),
    hasVendorCredentials: response.hasVendorCredentials ?? false,
    hasMarketerCredentials: response.hasMarketerCredentials ?? false,
    hasDeveloperCredentials: response.hasDeveloperCredentials ?? false,
    credentialType: response.credentialType,
    isMinor: response.isMinor ?? false,
    allowedTabs: response.allowedTabs ?? undefined,
    isJointContext: response.isJointAccountContext ?? false,
    jointAccountId: response.jointAccountId,
    jointAccountName: response.jointAccountName,
    aliasId: response.aliasId,
  };
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // IP verification state
  requiresIpVerification: boolean;
  ipVerificationToken: string | null;
  maskedEmail: string | null;
  pendingUserId: string | null;
  pendingUsername: string | null;
  // MFA state
  requiresMfa: boolean;
  mfaSessionToken: string | null;
  preferredMfaMethod: string | null;
  availableMfaMethods: string[];
  availablePasskeys: { passkeyId: string; label: string; transports?: string }[];
  mfaScopeCredentialId: string | null;
}

// Load user from localStorage if access token exists (for page refresh persistence).
// Sessions idle ≥12h (or missing last-activity) are treated as logged out.
const loadUserFromStorage = (): User | null => {
  if (expireStalePersistedSession()) {
    clearSharedAuthCookie();
    return null;
  }

  const accessToken = localStorage.getItem('accessToken');
  const userJson = localStorage.getItem('user');
  if (accessToken && userJson) {
    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }
  return null;
};

const storedUser = loadUserFromStorage();

const initialState: AuthState = {
  user: storedUser,
  isAuthenticated: storedUser !== null,
  isLoading: false,
  error: null,
  // IP verification state
  requiresIpVerification: false,
  ipVerificationToken: null,
  maskedEmail: null,
  pendingUserId: null,
  pendingUsername: null,
  requiresMfa: false,
  mfaSessionToken: null,
  preferredMfaMethod: null,
  availableMfaMethods: [],
  availablePasskeys: [],
  mfaScopeCredentialId: null,
};

// Extended result type for login that may require IP verification
interface LoginResult {
  user?: User;
  requiresIpVerification?: boolean;
  ipVerificationToken?: string;
  maskedEmail?: string;
  userId?: string;
  username?: string;
  requiresMfa?: boolean;
  mfaSessionToken?: string;
  preferredMfaMethod?: string;
  availableMfaMethods?: string[];
  availablePasskeys?: { passkeyId: string; label: string; transports?: string }[];
  mfaScopeCredentialId?: string | null;
}

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);

      if (response.requiresMfa) {
        return {
          requiresMfa: true,
          mfaSessionToken: response.mfaSessionToken,
          preferredMfaMethod: response.preferredMfaMethod,
          availableMfaMethods: response.availableMfaMethods || [],
          availablePasskeys: response.availablePasskeys || [],
          mfaScopeCredentialId: response.mfaScopeCredentialId || null,
          userId: response.userId,
          username: response.username,
        } as LoginResult;
      }
      
      // If IP verification is required, return that info instead of completing login
      if (response.requiresIpVerification) {
        return {
          requiresIpVerification: true,
          ipVerificationToken: response.ipVerificationToken,
          maskedEmail: response.maskedEmail,
          userId: response.userId,
          username: response.username,
        } as LoginResult;
      }
      
      const user = mapLoginResponseToUser(response);
      // Persist user to localStorage for page refresh
      localStorage.setItem('user', JSON.stringify(user));
      writeLastActivityAt();
      
      // Set shared auth cookie for SSO across all 2WayMicropay sites
      // This enables recognition in Products Portal (purchase/payout links)
      const loginAlias = (response.loginHandle || response.username || '').trim() || response.username;
      setSharedAuthCookie(response.userId, loginAlias, response.accessToken);
      
      return { user } as LoginResult;
    } catch (error: any) {
      console.error('❌ Login error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.response?.data?.message);
      const errorMessage = error.response?.data?.message
        || (error.message === 'Network Error'
          ? 'Could not reach the mail server. Check your connection and try again.'
          : error.message)
        || 'Login failed';
      return rejectWithValue(errorMessage);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      // Clear user from localStorage
      localStorage.removeItem('user');
      clearLastActivityAt();
      // Clear shared auth cookie to logout across all 2WayMicropay sites
      clearSharedAuthCookie();
      try {
        const { clearBiometricSessionUnlocked } = await import('../utils/biometricUnlock');
        clearBiometricSessionUnlocked();
      } catch {
        // ignore
      }
    } catch (error: any) {
      // Still clear user from localStorage even on error
      localStorage.removeItem('user');
      clearLastActivityAt();
      // Still clear shared cookie even on error
      clearSharedAuthCookie();
      try {
        const { clearBiometricSessionUnlocked } = await import('../utils/biometricUnlock');
        clearBiometricSessionUnlocked();
      } catch {
        // ignore
      }
      return rejectWithValue(error.response?.data?.message || 'Logout failed');
    }
  }
);

export const verifyIpLogin = createAsyncThunk(
  'auth/verifyIpLogin',
  async ({ token, code }: { token: string; code: string }, { rejectWithValue }) => {
    try {
      const response = await authService.verifyIpLogin({ token, code });
      
      const user = mapLoginResponseToUser(response);
      // Persist user to localStorage for page refresh
      localStorage.setItem('user', JSON.stringify(user));
      writeLastActivityAt();
      
      // Set shared auth cookie for SSO across all 2WayMicropay sites
      const loginAlias = (response.loginHandle || response.username || '').trim() || response.username;
      setSharedAuthCookie(response.userId, loginAlias, response.accessToken);
      
      return user;
    } catch (error: any) {
      console.error('❌ IP verification error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Verification failed';
      return rejectWithValue(errorMessage);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearIpVerification: (state) => {
      state.requiresIpVerification = false;
      state.ipVerificationToken = null;
      state.maskedEmail = null;
      state.pendingUserId = null;
      state.pendingUsername = null;
    },
    clearMfa: (state) => {
      state.requiresMfa = false;
      state.mfaSessionToken = null;
      state.preferredMfaMethod = null;
      state.availableMfaMethods = [];
      state.availablePasskeys = [];
      state.mfaScopeCredentialId = null;
      state.pendingUserId = null;
      state.pendingUsername = null;
    },
    applyAuthenticatedUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.requiresMfa = false;
      state.mfaSessionToken = null;
      state.preferredMfaMethod = null;
      state.availableMfaMethods = [];
      state.availablePasskeys = [];
      state.mfaScopeCredentialId = null;
      state.requiresIpVerification = false;
      state.ipVerificationToken = null;
      state.maskedEmail = null;
    },
    setIpVerificationFromMfa: (state, action: PayloadAction<{
      ipVerificationToken: string;
      maskedEmail?: string;
      userId?: string;
      username?: string;
    }>) => {
      state.requiresMfa = false;
      state.mfaSessionToken = null;
      state.requiresIpVerification = true;
      state.ipVerificationToken = action.payload.ipVerificationToken;
      state.maskedEmail = action.payload.maskedEmail || null;
      state.pendingUserId = action.payload.userId || null;
      state.pendingUsername = action.payload.username || null;
      state.isAuthenticated = false;
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        // Clear any previous IP verification state
        state.requiresIpVerification = false;
        state.ipVerificationToken = null;
        state.maskedEmail = null;
        state.pendingUserId = null;
        state.pendingUsername = null;
        state.requiresMfa = false;
        state.mfaSessionToken = null;
        state.preferredMfaMethod = null;
        state.availableMfaMethods = [];
        state.availablePasskeys = [];
        state.mfaScopeCredentialId = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;

        if (action.payload.requiresMfa) {
          state.requiresMfa = true;
          state.mfaSessionToken = action.payload.mfaSessionToken || null;
          state.preferredMfaMethod = action.payload.preferredMfaMethod || null;
          state.availableMfaMethods = action.payload.availableMfaMethods || [];
          state.availablePasskeys = action.payload.availablePasskeys || [];
          state.mfaScopeCredentialId = action.payload.mfaScopeCredentialId || null;
          state.pendingUserId = action.payload.userId || null;
          state.pendingUsername = action.payload.username || null;
          state.isAuthenticated = false;
          state.user = null;
        } else if (action.payload.requiresIpVerification) {
          state.requiresIpVerification = true;
          state.ipVerificationToken = action.payload.ipVerificationToken || null;
          state.maskedEmail = action.payload.maskedEmail || null;
          state.pendingUserId = action.payload.userId || null;
          state.pendingUsername = action.payload.username || null;
          // User is NOT authenticated yet
          state.isAuthenticated = false;
          state.user = null;
        } else if (action.payload.user) {
          // Normal successful login
          state.user = action.payload.user;
          state.isAuthenticated = true;
          state.requiresIpVerification = false;
          state.requiresMfa = false;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(logout.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
      })
      // Verify IP Login
      .addCase(verifyIpLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyIpLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        // Clear IP verification state
        state.requiresIpVerification = false;
        state.ipVerificationToken = null;
        state.maskedEmail = null;
        state.pendingUserId = null;
        state.pendingUsername = null;
      })
      .addCase(verifyIpLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        // Keep IP verification state so user can retry
      });
  },
});

export const { setUser, clearUser, clearError, clearIpVerification, clearMfa, applyAuthenticatedUser, setIpVerificationFromMfa } = authSlice.actions;
export default authSlice.reducer;

